/**
 * One parent send per student per class session.
 *
 * notifyAt is the later of 20 minutes after class end and 10 minutes after
 * the last add or edit. A time outside 7:00am–9:00pm Pacific waits until
 * 7:00am. The card reads this instant; the server sends at it.
 */

export const FAMILY_DELIVERY_COLLECTION = "familyDeliveries"

export type FamilyDeliveryStatus = "scheduled" | "sent" | "cancelled"

export interface FamilyDeliveryConstructor {
  id: string
  studentId: string
  classSessionId: string
  notifyAt: string
  status: FamilyDeliveryStatus
  sentAt?: string | null
}

const TEN_MINUTES_MS = 10 * 60 * 1000
const TWENTY_MINUTES_MS = 20 * 60 * 1000
const OPEN_START_MINUTE = 7 * 60
const OPEN_END_MINUTE = 21 * 60

const pacificFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
})

export type PacificParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export const pacificParts = (instant: Date): PacificParts => {
  const map: Record<string, string> = {}
  for (const part of pacificFormatter.formatToParts(instant)) {
    map[part.type] = part.value
  }
  let hour = Number(map.hour)
  if (hour === 24) hour = 0
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour,
    minute: Number(map.minute),
    second: Number(map.second),
  }
}

/** 7:00:00.000 America/Los_Angeles on the given school day, as UTC. */
export const pacificSevenAmUtc = (
  year: number,
  month: number,
  day: number
): Date => {
  const guess = Date.UTC(year, month - 1, day, 15, 0, 0)
  const seen = pacificParts(new Date(guess))
  const seenAsUtc = Date.UTC(
    seen.year,
    seen.month - 1,
    seen.day,
    seen.hour,
    seen.minute,
    seen.second
  )
  const wantedAsUtc = Date.UTC(year, month - 1, day, 7, 0, 0)
  return new Date(guess - (seenAsUtc - wantedAsUtc))
}

const dayKey = (parts: PacificParts): number =>
  parts.year * 10000 + parts.month * 100 + parts.day

const addSchoolDay = (parts: PacificParts): PacificParts => {
  const utc =
    Date.UTC(parts.year, parts.month - 1, parts.day) + 24 * 60 * 60 * 1000
  const next = new Date(utc)
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
    hour: 0,
    minute: 0,
    second: 0,
  }
}

/**
 * Times before 7:00am move to 7:00am that morning. Times at or after 9:00pm
 * move to 7:00am the next morning. 7:00am itself is allowed.
 */
export const clampToSchoolHours = (instant: Date): Date => {
  const local = pacificParts(instant)
  const minuteOfDay = local.hour * 60 + local.minute
  if (minuteOfDay >= OPEN_START_MINUTE && minuteOfDay < OPEN_END_MINUTE) {
    return instant
  }
  if (minuteOfDay < OPEN_START_MINUTE) {
    return pacificSevenAmUtc(local.year, local.month, local.day)
  }
  const next = addSchoolDay(local)
  return pacificSevenAmUtc(next.year, next.month, next.day)
}

/**
 * Later of class end + 20 minutes and now + 10 minutes, then quiet hours.
 * A missing class end uses now + 10 minutes only.
 */
export const nextNotifyAt = (
  eventEndIso: string | null | undefined,
  now: Date
): string => {
  const fromNow = now.getTime() + TEN_MINUTES_MS
  const classEnd = eventEndIso ? Date.parse(eventEndIso) : Number.NaN
  const fromClass = Number.isFinite(classEnd)
    ? classEnd + TWENTY_MINUTES_MS
    : fromNow
  return clampToSchoolHours(
    new Date(Math.max(fromNow, fromClass))
  ).toISOString()
}

export const familyDeliveryId = (
  studentId: string,
  classSessionId: string
): string => `${studentId}__${classSessionId}`

export type SendCountdown =
  | { kind: "morning" }
  | { kind: "minutes"; count: number }
  | { kind: "seconds"; count: number }

/** School-calendar tomorrow uses the morning phrase. Same day counts down. */
export const sendCountdown = (
  notifyAtIso: string,
  now: Date
): SendCountdown => {
  const notifyAt = new Date(notifyAtIso)
  if (dayKey(pacificParts(notifyAt)) > dayKey(pacificParts(now))) {
    return { kind: "morning" }
  }
  const remaining = notifyAt.getTime() - now.getTime()
  if (remaining >= 60_000) {
    return { kind: "minutes", count: Math.floor(remaining / 60_000) }
  }
  return {
    kind: "seconds",
    count: Math.max(0, Math.floor(remaining / 1000)),
  }
}

/**
 * Milliseconds until the minutes label should change.
 * At an exact minute the label is still "N minutes" (`remaining >= 60s`).
 * Waiting that whole minute would leave "sending in 1 minute" up through
 * the final minute. One millisecond crosses into the next floor, and into
 * the seconds countdown when that minute was the last one. Any other
 * remainder waits until the next minute boundary.
 */
export const countdownTickDelayMs = (remainingMs: number): number => {
  const partial = remainingMs % 60_000
  return partial === 0 ? 1 : partial
}

/**
 * Class ends the subscription has already loaded, plus ends read for
 * session ids that were not in that map yet. An id that was loaded with
 * no `eventEnd` stays present so the caller can tell "known missing" from
 * "not loaded". An id that still has no document stays absent.
 */
export const eventEndsWithLoadedSessions = (
  known: Record<string, string | null | undefined>,
  sessionIds: readonly string[],
  loaded: readonly { id: string; eventEnd?: string | null }[]
): Record<string, string | null | undefined> => {
  const eventEnds = { ...known }
  const loadedById = new Map(loaded.map((session) => [session.id, session]))
  for (const id of sessionIds) {
    if (!id || id in known) continue
    const session = loadedById.get(id)
    if (!session) continue
    eventEnds[id] = session.eventEnd ?? null
  }
  return eventEnds
}

/** Session ends already in the subscription. An id that is not here is not loaded yet. */
export const knownEventEndsBySessionId = (
  sessionsByClassId: Record<
    string,
    readonly { id: string; eventEnd?: string | null }[]
  >
): Record<string, string | null | undefined> => {
  const known: Record<string, string | null | undefined> = {}
  for (const sessions of Object.values(sessionsByClassId)) {
    for (const session of sessions) {
      known[session.id] = session.eventEnd ?? null
    }
  }
  return known
}

export const eventEndForSession = (
  sessionsByClassId: Record<
    string,
    readonly { id: string; eventEnd?: string }[]
  >,
  classSessionId: string | null | undefined
): string | null => {
  if (!classSessionId) return null
  for (const sessions of Object.values(sessionsByClassId)) {
    const match = sessions.find((session) => session.id === classSessionId)
    if (match?.eventEnd) return match.eventEnd
  }
  return null
}
